import { useGlobalStore } from "@/store/global-store";
import { TProductMin, TGetProducts, TProduct } from "@/types/productv2";
import { TCategory } from "@/types/products";
import { useEffect, useState } from "react";

// Defined a type that extends TProductMin with the additional properties your hook needs
export type TFormattedProduct = TProductMin & {
	categories: TCategory[];
	isUpvoted: boolean;
	votesCount: number;
	commentsCount: number;
	companySlug: string;
};

const isProductUpvotedByUser = (userVotes: string[], productId: string) => {
	const isUpvoted = userVotes?.find((vote) => vote === productId);
	return isUpvoted ? true : false;
};

const isTGetProducts = (data: TGetProducts | TProductMin[]): data is TGetProducts => {
	if (!data || data.length === 0) return false;
	const firstItem = data[0] as any;
	return (
		firstItem.regions &&
		Array.isArray(firstItem.regions) &&
		firstItem.regions[0] &&
		typeof firstItem.regions[0] === "object" &&
		firstItem.regions[0].region
	);
};

const getProductCategories = (product: any): TCategory[] => {
	if (product.parentCategories && Array.isArray(product.parentCategories)) {
		return product.parentCategories;
	}
	if (product.categories && Array.isArray(product.categories)) {
		return product.categories;
	}
	if (product.productCategories && Array.isArray(product.productCategories)) {
		return product.productCategories;
	}
	return [];
};

const getCompanySlug = (product: any): string => {
	if (product.companySlug) {
		return product.companySlug;
	}
	if (product.Company && product.Company.slug) {
		return product.Company.slug;
	}
	if (product.company && product.company.slug) {
		return product.company.slug;
	}
	return "";
};

const calculateTotalRating = (product: any): number => {
	if (product.ProductReview && Array.isArray(product.ProductReview)) {
		const reviews = product.ProductReview.filter((review: any) => review.isVisible === true);
		if (reviews.length === 0) return 0;

		const totalRating =
			reviews.reduce((sum: number, review: any) => sum + (review.overallRating || 0), 0) /
			reviews.length;
		return parseFloat(totalRating.toFixed(1));
	}
	if (typeof product.totalRating === "number") {
		return product.totalRating;
	}

	return 0;
};

const formatTags = (tags: any[]): { name: string; type: string }[] => {
	if (!Array.isArray(tags)) return [];

	return tags.map((tag) => {
		if (tag.type) {
			return {
				name: tag.name,
				type: tag.type,
			};
		}
		if (tag.tag && tag.tag.name) {
			return {
				name: tag.tag.name,
				type: "tag",
			};
		}
		if (tag.name) {
			return {
				name: tag.name,
				type: "tag",
			};
		}
		if (typeof tag === "string") {
			return {
				name: tag,
				type: "tag",
			};
		}

		return {
			name: tag.name || tag,
			type: "tag",
		};
	});
};

const useFormattedProductsData = (products: TGetProducts | TProductMin[]) => {
	const { userVotes = [], productVotes = [], productsCommentCount = [] } = useGlobalStore();
	const [formattedProducts, setFormattedProducts] = useState<TFormattedProduct[]>([]);

	useEffect(() => {
		if (!products || products.length === 0) {
			setFormattedProducts([]);
			return;
		}

		if (isTGetProducts(products)) {
			const formattedProducts = products.map(
				(product): TFormattedProduct => ({
					id: product.id.toString(),
					slug: product.slug,
					logo: product.logo,
					name: product.name,
					tagline: product.tagline,
					description: product.description,
					categories: getProductCategories(product),
					hqLocation: product.hqLocation || "",
					isUpvoted: isProductUpvotedByUser(userVotes, product.id),
					votesCount: productVotes?.find((vote) => vote.productId === product.id)?.totalVotes || 0,
					commentsCount:
						productsCommentCount?.find((comment) => comment.productId === product.id)?.count || 0,
					industriesServed: product.industriesServed || [],
					tags: formatTags(product.tags || []),
					totalRating: calculateTotalRating(product),
					companySlug: getCompanySlug(product),
				})
			);
			setFormattedProducts(formattedProducts);
		} else {
			const formattedProducts = (products as TProduct[]).map((product): TFormattedProduct => {
				const categories = getProductCategories(product);

				const transformedCategories = categories.map((cat: any) => {
					if (cat.productId && cat.parentCategoryId && cat.parentCategory) {
						return cat;
					}
					if (cat.id && cat.name && cat.slug) {
						return {
							productId: product.id,
							parentCategoryId: cat.id,
							parentCategory: {
								name: cat.name,
								slug: cat.slug,
							},
						};
					}
					return cat;
				});

				return {
					id: product.id,
					slug: product.slug,
					logo: product.logo,
					name: product.name,
					tagline: product.tagline,
					description: product.description || "",
					categories: transformedCategories,
					hqLocation: product.hqLocation,
					isUpvoted: isProductUpvotedByUser(userVotes, product.id),
					votesCount:
						productVotes?.find((vote) => vote.productId === product.id)?.totalVotes ||
						(product as any).upvotes ||
						0,
					commentsCount:
						productsCommentCount?.find((comment) => comment.productId === product.id)?.count || 0,
					industriesServed: product.industriesServed || [],
					tags: formatTags(product.tags || []),
					totalRating: calculateTotalRating(product),
					companySlug: getCompanySlug(product),
				};
			});
			setFormattedProducts(formattedProducts);
		}
	}, [products, userVotes, productVotes, productsCommentCount]);

	return formattedProducts;
};

export default useFormattedProductsData;
